const readableElementSelector = 'h1, h2, h3, h4, h5, h6, p, li, blockquote, figcaption, td, th';
const excludedElementSelector = [
    '[data-reader-ignore]',
    '[aria-hidden="true"]',
    'script',
    'style',
    'noscript',
    'form',
    'nav',
    'button',
    'input',
    'select',
    'textarea',
    'label',
].join(', ');

const normalizeText = (value) => value?.replace(/\s+/g, ' ').trim() ?? '';

const splitText = (text, maximumLength = 220) => {
    if (text.length <= maximumLength) {
        return [text];
    }

    const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g) ?? [text];
    const segments = [];
    let currentSegment = '';

    sentences.forEach((sentence) => {
        const normalizedSentence = normalizeText(sentence);
        if (!normalizedSentence) {
            return;
        }

        if (`${currentSegment} ${normalizedSentence}`.trim().length <= maximumLength) {
            currentSegment = `${currentSegment} ${normalizedSentence}`.trim();
            return;
        }

        if (currentSegment) {
            segments.push(currentSegment);
        }

        if (normalizedSentence.length <= maximumLength) {
            currentSegment = normalizedSentence;
            return;
        }

        const words = normalizedSentence.split(' ');
        currentSegment = '';

        words.forEach((word) => {
            if (`${currentSegment} ${word}`.trim().length > maximumLength && currentSegment) {
                segments.push(currentSegment);
                currentSegment = word;
            } else {
                currentSegment = `${currentSegment} ${word}`.trim();
            }
        });
    });

    if (currentSegment) {
        segments.push(currentSegment);
    }

    return segments;
};

const getLanguageCandidates = (language) => {
    const normalizedLanguage = language.toLowerCase().replace('_', '-');
    const baseLanguage = normalizedLanguage.split('-')[0];
    const baseLanguages = ['no', 'nb', 'nn'].includes(baseLanguage)
        ? ['no', 'nb', 'nn']
        : [baseLanguage];

    return { normalizedLanguage, baseLanguages };
};

export const selectVoice = (voices, language) => {
    const { normalizedLanguage, baseLanguages } = getLanguageCandidates(language);

    return voices.find((voice) => voice.lang.toLowerCase().replace('_', '-') === normalizedLanguage)
        ?? voices.find((voice) => baseLanguages.includes(voice.lang.toLowerCase().split(/[-_]/)[0]))
        ?? null;
};

export default () => ({
    available: false,
    currentSegmentIndex: 0,
    isOpen: false,
    isPaused: false,
    isPlaying: false,
    labels: {},
    language: '',
    originalBodyPaddingBottom: '',
    segments: [],
    statusMessage: '',
    synth: null,
    title: '',
    voice: null,
    voicesChangedHandler: null,

    init() {
        const { dataset } = this.$root;

        this.language = dataset.readerLanguage || document.documentElement.lang || 'da-DK';
        this.title = normalizeText(dataset.readerTitle);
        this.labels = {
            finished: dataset.labelFinished,
            pause: dataset.labelPause,
            paused: dataset.labelPaused,
            resume: dataset.labelResume,
            started: dataset.labelStarted,
            stop: dataset.labelStop,
            stopped: dataset.labelStopped,
            unavailable: dataset.labelUnavailable,
        };

        if (!('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) {
            return;
        }

        this.synth = window.speechSynthesis;
        this.resolveVoice();

        this.voicesChangedHandler = () => this.resolveVoice();
        this.synth.addEventListener('voiceschanged', this.voicesChangedHandler);

        window.addEventListener('pagehide', () => this.stop(false, false), { once: true });
    },

    destroy() {
        this.stop(false, false);

        if (this.synth && this.voicesChangedHandler) {
            this.synth.removeEventListener('voiceschanged', this.voicesChangedHandler);
        }
    },

    resolveVoice() {
        const voices = this.synth?.getVoices() ?? [];
        this.voice = selectVoice(voices, this.language);
        this.available = this.voice !== null;
    },

    toggle() {
        if (!this.isOpen) {
            this.start();
        } else if (this.isPaused) {
            this.resume();
        } else {
            this.pause();
        }
    },

    start() {
        if (!this.synth || !this.voice) {
            this.statusMessage = this.labels.unavailable;
            return;
        }

        this.segments = this.buildSegments();
        if (this.segments.length === 0) {
            this.statusMessage = this.labels.unavailable;
            return;
        }

        this.synth.cancel();
        this.currentSegmentIndex = 0;
        this.isOpen = true;
        this.isPaused = false;
        this.isPlaying = true;
        this.statusMessage = this.labels.started;
        this.originalBodyPaddingBottom = document.body.style.paddingBottom;
        document.body.style.paddingBottom = '6rem';
        this.speakCurrentSegment();
    },

    pause() {
        if (!this.synth || !this.isPlaying || this.isPaused) {
            return;
        }

        this.synth.pause();
        this.isPaused = true;
        this.statusMessage = this.labels.paused;
    },

    resume() {
        if (!this.synth || !this.isPaused) {
            return;
        }

        this.synth.resume();
        this.isPaused = false;
        this.statusMessage = this.labels.started;
    },

    stop(returnFocus = false, announce = true) {
        this.synth?.cancel();
        this.isOpen = false;
        this.isPaused = false;
        this.isPlaying = false;
        this.currentSegmentIndex = 0;
        document.body.style.paddingBottom = this.originalBodyPaddingBottom;

        if (announce) {
            this.statusMessage = this.labels.stopped;
        }

        if (returnFocus) {
            this.$nextTick(() => this.$refs.readerTrigger?.focus());
        }
    },

    buildSegments() {
        const content = document.querySelector(this.$root.dataset.readerContentSelector);
        if (!content) {
            return [];
        }

        const intro = normalizeText(this.$root.dataset.readerIntro);
        const textSegments = [this.title, intro];

        content.querySelectorAll(readableElementSelector).forEach((element) => {
            if (element.closest(excludedElementSelector) || element.querySelector(readableElementSelector)) {
                return;
            }

            const text = normalizeText(element.textContent);
            if (text) {
                textSegments.push(text);
            }
        });

        return textSegments.flatMap((text) => splitText(text)).filter(Boolean);
    },

    speakCurrentSegment() {
        if (!this.synth || !this.voice || !this.isOpen) {
            return;
        }

        if (this.currentSegmentIndex >= this.segments.length) {
            this.finish();
            return;
        }

        const utterance = new SpeechSynthesisUtterance(this.segments[this.currentSegmentIndex]);
        utterance.lang = this.voice.lang || this.language;
        utterance.voice = this.voice;
        utterance.onend = () => {
            if (!this.isOpen) {
                return;
            }

            this.currentSegmentIndex += 1;
            this.speakCurrentSegment();
        };
        utterance.onerror = (event) => {
            if (!['canceled', 'interrupted'].includes(event.error)) {
                this.statusMessage = this.labels.unavailable;
                this.stop(false, false);
            }
        };

        this.synth.speak(utterance);
    },

    finish() {
        this.isOpen = false;
        this.isPaused = false;
        this.isPlaying = false;
        document.body.style.paddingBottom = this.originalBodyPaddingBottom;
        this.statusMessage = this.labels.finished;
    },
});
